import * as React from 'react'
import {
    Box,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'

function FieldLabel({ children, required = false }) {
    return (
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {children}
            {required && (
                <Box component="span" sx={{ color: '#ff6b6b', ml: 0.5 }}>
                    *
                </Box>
            )}
        </Typography>
    )
}

function FieldHint({ children }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.45 }}>
                {children}
            </Typography>
        </Stack>
    )
}

export default function UserCredentialsPanel({
    username,
    password,
    onUsernameChange,
    onPasswordChange,
    passwordHidden = false,
    passwordDisabled = false,
    appLanguage = 'en'
}) {
    const [showPassword, setShowPassword] = React.useState(false)
    const t = useAutoTranslate(appLanguage, [
        'Username',
        'Password',
        'Your game username',
        'Your game password',
        'Password hidden for bots owned by another account.'
    ])

    return (
        <Stack spacing={2.2} sx={{ pt: 0.25 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.2}>
                <Stack spacing={0.75} sx={{ flex: 1 }}>
                    <FieldLabel required>{t('Username')}</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={username}
                        onChange={event => onUsernameChange?.(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8
                            }
                        }}
                    />
                    <FieldHint>{t('Your game username')}</FieldHint>
                </Stack>

                <Stack spacing={0.75} sx={{ flex: 1 }}>
                    <FieldLabel required>{t('Password')}</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        type={showPassword ? 'text' : 'password'}
                        value={passwordHidden ? '' : password}
                        placeholder={passwordHidden ? '********' : ''}
                        disabled={passwordDisabled}
                        onChange={event => onPasswordChange?.(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => setShowPassword(previous => !previous)}
                                        disabled={passwordDisabled || passwordHidden}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <FieldHint>{passwordHidden ? t('Password hidden for bots owned by another account.') : t('Your game password')}</FieldHint>
                </Stack>
            </Stack>
        </Stack>
    )
}
